import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db5mfm9uu2cnvnf27jtg";

type Status = "idle" | "submitting" | "success" | "error";

export default function SubscribeSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    // honeypot — silently look like success for bots
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const email = String(formData.get("email") ?? "").trim();
    const topic = String(formData.get("topic") ?? "").trim();
    const nickname = String(formData.get("nickname") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError("请填写有效的邮箱地址，方便我们把更新发给你。");
      setStatus("error");
      return;
    }
    if (!topic) {
      setFormError("请选择你这次想做的类型。");
      setStatus("error");
      return;
    }
    if (message.length > 500) {
      setFormError("留言请不要超过 500 字。");
      setStatus("error");
      return;
    }

    const payload = new URLSearchParams();
    if (email) payload.append("email", email);
    if (topic) payload.append("topic", topic);
    if (nickname) payload.append("nickname", nickname);
    if (message) payload.append("message", message);

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        form.reset();
      } else {
        setFormError(
          serverMsg && !isSpam
            ? serverMsg
            : "提交没有成功，请稍后重试，或换一个邮箱地址再试一次。"
        );
        setStatus("error");
      }
    } catch {
      setFormError("网络似乎不太稳定，请稍后重试。");
      setStatus("error");
    }
  };

  return (
    <section id="subscribe" className="w-full bg-background-100 py-16 md:py-20">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-8">
        <div className="grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 lg:grid-cols-2">
          <div className="relative min-h-[240px] lg:min-h-full">
            <img
              src="https://readdy.ai/api/search-image?query=Cozy%20warm%20top%20down%20table%20setting%20with%20several%20appetizing%20home%20cooked%20dishes%20and%20a%20notebook%20for%20notes%2C%20soft%20natural%20window%20light%2C%20neutral%20beige%20tones%2C%20editorial%20food%20blog%20photography&width=1000&height=1200&seq=subscribe-food-table-31&orientation=portrait"
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/30 to-transparent" />
            <div className="relative flex h-full flex-col justify-end gap-3 p-6 md:p-8">
              <span className="inline-flex w-fit items-center gap-2 rounded-full bg-background-50/15 px-3 py-1 text-xs font-medium text-background-100">
                <i className="ri-mail-send-line" aria-hidden="true" />
                订阅与投稿
              </span>
              <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
                想看的店、想问的价，直接告诉我们
              </h2>
              <p className="max-w-md text-sm leading-relaxed text-background-200">
                留下邮箱可以收到每周测评更新；也想推荐店铺、点题或投稿，就在留言里写清楚，我们会挑高频选题优先安排。
              </p>
            </div>
          </div>

          <div className="bg-background-50 p-6 md:p-8">
            <form id="subscribe-form" data-readdy-form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="text-xs font-medium text-foreground-700">
                    邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="nickname" className="text-xs font-medium text-foreground-700">
                    称呼（选填）
                  </label>
                  <input
                    id="nickname"
                    name="nickname"
                    type="text"
                    autoComplete="off"
                    placeholder="怎么称呼你"
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="topic" className="text-xs font-medium text-foreground-700">
                  这次想做 <span className="text-primary-600">*</span>
                </label>
                <select
                  id="topic"
                  name="topic"
                  defaultValue=""
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                >
                  <option value="" disabled>
                    请选择
                  </option>
                  <option value="订阅每周测评更新">订阅每周测评更新</option>
                  <option value="推荐一家外卖或店铺">推荐一家外卖或店铺</option>
                  <option value="点题想看的开箱商品">点题想看的开箱商品</option>
                  <option value="投稿一篇测评">投稿一篇测评</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="message" className="text-xs font-medium text-foreground-700">
                  留言（选填，500 字以内）
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="比如：想看看城南那家新开的螺蛳粉到底值不值得排队……"
                  className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              <div className="form-alt-field" aria-hidden="true">
                <label htmlFor="website_alt">请勿填写此栏</label>
                <input
                  id="website_alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                  aria-hidden="true"
                />
              </div>

              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} aria-hidden="true" />
                {status === "submitting" ? "正在提交…" : "提交"}
              </button>

              {status === "success" ? (
                <p className="flex items-start gap-2 rounded-md bg-primary-50 px-3 py-2.5 text-xs leading-relaxed text-primary-800">
                  <i className="ri-checkbox-circle-line mt-0.5" aria-hidden="true" />
                  提交成功，谢谢你的支持！我们会认真看每一条留言。
                </p>
              ) : null}
              {status === "error" ? (
                <p className="flex items-start gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-xs leading-relaxed text-secondary-900">
                  <i className="ri-error-warning-line mt-0.5" aria-hidden="true" />
                  {formError}
                </p>
              ) : null}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}